import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Badge from '@mui/joy/Badge';
import { formatLogTimestamp } from '../../helpers/utils';

const STATUS_CONFIG = {
  running: {
    color: 'primary',
    icon: <CircularProgress size="sm" sx={{ '--CircularProgress-size': '16px' }} />,
    label: 'Running',
  },
  success: {
    color: 'success',
    icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
    label: 'Success',
  },
  error: {
    color: 'danger',
    icon: <ErrorIcon sx={{ fontSize: 16 }} />,
    label: 'Error',
  },
  idle: {
    color: 'neutral',
    icon: <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />,
    label: 'Never run',
  },
};

const TYPE_CONFIG = {
  scheduled: {
    color: 'primary',
    icon: <ScheduleIcon sx={{ fontSize: 14 }} />,
    label: 'Scheduled',
  },
  'post-complete': {
    color: 'success',
    icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
    label: 'Post-Complete',
  },
};

const FunctionCard = ({
  func,
  status,
  isRunning,
  onRun,
  onEdit,
  onDuplicate,
  onDelete,
  onViewLogs,
  selected,
  onSelect,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);

  const typeConfig = TYPE_CONFIG[func.function_type] || TYPE_CONFIG['post-complete'];
  const statusConfig = STATUS_CONFIG[status?.status] || STATUS_CONFIG.idle;

  const hasUnreadLogs = status?.hasUnread;
  const lastExecuted = func.last_executed ? formatLogTimestamp(func.last_executed) : null;

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleAction = (action) => (event) => {
    event.stopPropagation();
    handleMenuClose();
    action(func);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        borderColor: selected ? 'primary.500' : 'divider',
        bgcolor: selected ? 'primary.softBg' : 'background.surface',
        '&:hover': {
          borderColor: 'primary.300',
          boxShadow: 'sm',
        },
      }}
      onClick={() => onSelect?.(func)}
    >
      {/* Header with type badge and status */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Chip
          size="sm"
          variant="soft"
          color={typeConfig.color}
          startDecorator={typeConfig.icon}
        >
          {typeConfig.label}
        </Chip>
        <Tooltip title={statusConfig.label}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: `${statusConfig.color}.500`,
            }}
          >
            {statusConfig.icon}
          </Box>
        </Tooltip>
      </Box>

      <CardContent sx={{ flex: 1, pt: 0 }}>
        {/* Function name */}
        <Typography
          level="title-md"
          sx={{
            fontWeight: 600,
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {func.name}
        </Typography>

        {/* Description */}
        <Typography
          level="body-sm"
          sx={{
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '2.6em',
            mb: 1,
          }}
        >
          {func.description || 'No description'}
        </Typography>

        {/* Meta info */}
        <Stack spacing={0.5}>
          {func.function_type === 'scheduled' && func.interval && (
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              Runs every {formatInterval(func.interval)}
            </Typography>
          )}
          {lastExecuted && (
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              Last run: {lastExecuted}
            </Typography>
          )}
        </Stack>
      </CardContent>

      <Divider />

      {/* Actions */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Run function">
            <IconButton
              size="sm"
              variant="plain"
              color="primary"
              onClick={handleAction(onRun)}
              disabled={isRunning}
            >
              {isRunning ? (
                <CircularProgress size="sm" sx={{ '--CircularProgress-size': '18px' }} />
              ) : (
                <PlayArrowIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit function">
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={handleAction(onEdit)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View logs">
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={handleAction(onViewLogs)}
            >
              {hasUnreadLogs ? (
                <Badge
                  variant="solid"
                  color="primary"
                  badgeContent=""
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  sx={{ '& .MuiBadge-badge': { width: 8, height: 8, p: 0, minWidth: 0 } }}
                >
                  <HistoryIcon fontSize="small" />
                </Badge>
              ) : (
                <HistoryIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>

        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={handleMenuOpen}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>

        <Menu
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={handleMenuClose}
          placement="bottom-end"
          sx={{ minWidth: 140 }}
        >
          <MenuItem onClick={handleAction(onDuplicate)}>
            <ContentCopyIcon fontSize="small" sx={{ mr: 1.5 }} />
            Duplicate
          </MenuItem>
          <MenuItem onClick={handleAction(onDelete)} color="danger">
            <DeleteIcon fontSize="small" sx={{ mr: 1.5, color: 'danger.500' }} />
            <Typography color="danger">Delete</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Card>
  );
};

// Helper function to format interval
const formatInterval = (seconds) => {
  if (!seconds || isNaN(seconds)) return '-';
  
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
};

export default FunctionCard;
